import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";
import { getAuthToken } from "@/lib/auth";
import { useChatStore } from "@/stores/chat-store";
import { useThreadStore } from "@/stores/thread-store";
import { trackEvent } from "@/lib/engagement";
import type { SourceAttribution } from "@/types/paper";
import { createThread, saveMessages } from "@/services/thread-service";

interface SendMessageOptions {
  mode?: "standard" | "contrastive";
  roleFilter?: string[];
}

export function useChat(conceptId: string, paperIds: string[] = []) {
  const queryClient = useQueryClient();

  const sendMessage = useCallback(
    async (message: string, options?: SendMessageOptions) => {
      trackEvent("feature_use", { feature: "chat", action: "started", mode: options?.mode ?? "standard" });
      const {
        messages,
        addUserMessage,
        startStreaming,
        appendToken,
        completeStreaming,
        failStreaming,
      } = useChatStore.getState();

      addUserMessage(message);

      // Build history from existing messages (exclude the one we just added)
      const history = messages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      let streamedSources: SourceAttribution[] = [];

      try {
        startStreaming();

        const baseUrl =
          import.meta.env.VITE_API_URL ?? "http://localhost:8000";
        const token = await getAuthToken();

        const res = await fetch(
          `${baseUrl}/api/concepts/${conceptId}/chat/stream`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
              Accept: "text/event-stream",
            },
            body: JSON.stringify({
              message,
              history,
              mode: options?.mode ?? "standard",
              role_filter: options?.roleFilter?.length ? options.roleFilter : null,
              paper_ids: paperIds,
            }),
          },
        );

        if (!res.ok) {
          const errText = await res.text();
          failStreaming(errText || `Chat failed: ${res.status}`);
          toast.error("Chat failed", {
            description: errText || `Status ${res.status}`,
          });
          return;
        }

        const reader = res.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let currentEvent = "";
        let currentData = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() ?? "";

          for (const line of lines) {
            if (line.startsWith("event: ")) {
              currentEvent = line.slice(7).trim();
            } else if (line.startsWith("data: ")) {
              currentData = line.slice(6).trim();
            } else if (line.startsWith(":")) {
              continue; // keepalive
            } else if (line === "") {
              if (currentEvent === "token" && currentData) {
                const data = JSON.parse(currentData);
                appendToken(data.text);
              } else if (currentEvent === "sources" && currentData) {
                const sources: SourceAttribution[] = JSON.parse(currentData);
                streamedSources = sources;
                completeStreaming(sources);
              } else if (currentEvent === "done") {
                // Stream complete
              }
              currentEvent = "";
              currentData = "";
            }
          }
        }

        // If we get here without completeStreaming being called
        const state = useChatStore.getState();
        if (state.isStreaming) {
          completeStreaming([]);
        }

        // Persist to thread after successful streaming (fire-and-forget)
        try {
          await _persistExchange(conceptId, message, streamedSources);
          queryClient.invalidateQueries({
            queryKey: ["chat-threads", conceptId],
          });
        } catch {
          toast.warning("Chat saved locally but failed to sync to server");
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Chat request failed";
        failStreaming(msg);
        toast.error("Chat error", { description: msg });
      }
    },
    [conceptId, queryClient, paperIds],
  );

  const clearChat = useCallback(() => {
    useChatStore.getState().clearChat();
    useThreadStore.getState().setActiveThreadId(null);
  }, []);

  return { sendMessage, clearChat };
}

/**
 * After a successful streaming exchange, persist the user and assistant
 * messages to a thread. Auto-creates a thread if none is active.
 */
async function _persistExchange(
  conceptId: string,
  userMessage: string,
  sources: SourceAttribution[],
) {
  let threadId = useThreadStore.getState().activeThreadId;

  // Auto-create thread from first message
  if (!threadId) {
    const title =
      userMessage.length > 50
        ? userMessage.slice(0, 50).trimEnd() + "..."
        : userMessage;
    const thread = await createThread(conceptId, { title });
    threadId = thread.id;
    useThreadStore.getState().setActiveThreadId(threadId);
  }

  // Get the last assistant message content
  const state = useChatStore.getState();
  const lastMsg = state.messages[state.messages.length - 1];
  const assistantContent =
    lastMsg?.role === "assistant" ? lastMsg.content : "";

  await saveMessages(conceptId, threadId, {
    messages: [
      { role: "user", content: userMessage },
      { role: "assistant", content: assistantContent, sources },
    ],
  });
}
